// pages/secure/UserAccountsPage.js
// Portal login accounts — create, link to a person, reset, revoke, restore.
//
// Managing broker only. Until this existed, giving someone a login required a
// developer to insert a database row by hand, which is not a workable way to
// onboard or offboard anyone.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import AgentInvitesPanel from '../../components/secure/AgentInvitesPanel';
import DataList from '../../components/shared/DataList';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '');

const STATUS_TONE = {
    active: 'bg-green-100 text-green-700',
    revoked: 'bg-slate-200 text-slate-600',
    expired: 'bg-amber-100 text-amber-800',
};

const ROLE_HELP = {
    admin: 'Staff. Full access, subject to the staff-role tier on their employee record.',
    licensee: 'A sponsored agent. Sees their own clients, properties and transactions — plus their team’s if they lead one.',
    examiner: 'IDFPR examiner. Read-only, no financial or HR areas, and expires on the date you set.',
};

const UserAccountsPage = () => {
    const api = useApiClient();
    const [users, setUsers] = useState([]);
    const [unlinked, setUnlinked] = useState({ employees: [], agents: [] });
    const [form, setForm] = useState(null);
    const [credentials, setCredentials] = useState(null);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);
    const [tfPolicy, setTfPolicy] = useState(null);
    const [pwFree, setPwFree] = useState(null);   // { user, devices, passkeys } — Password-free sign-in of one login

    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 6000); };
    const fail = (e) => setError(e.message || String(e));

    const load = useCallback(() => {
        setError('');
        api.get('/api/users').then(setUsers).catch(fail);
        api.get('/api/users/unlinked').then(setUnlinked).catch(fail);
        api.get('/api/users/two-factor-policy').then(setTfPolicy).catch(() => setTfPolicy(null));
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const openNew = (person) => setForm({
        username: '', role: person?.kind === 'agent' ? 'licensee' : 'admin', email: person?.email || '',
        employee_id: person?.kind === 'employee' ? person.id : '',
        agent_id: person?.kind === 'agent' ? person.id : '',
        _personName: person?.name || '',
    });

    const save = async () => {
        setBusy(true); setError('');
        try {
            const payload = { ...form };
            delete payload._personName;
            if (!payload.employee_id) delete payload.employee_id;
            if (!payload.agent_id) delete payload.agent_id;

            if (form.id) {
                await api.put(`/api/users/${form.id}`, payload);
                note('Login updated.');
                setForm(null);
            } else {
                const r = await api.post('/api/users', payload);
                setForm(null);
                setCredentials(r);   // shown once
            }
            load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const resetPassword = async (u) => {
        if (!window.confirm(`Reset the password for ${u.username}?\n\nTheir current password stops working immediately, and any outstanding reset link is cancelled.`)) return;
        try {
            const r = await api.post(`/api/users/${u.id}/reset-password`, {});
            setCredentials(r);
            load();
        } catch (e) { fail(e); }
    };

    // Lost or replaced phone. Confirm who is asking before doing this — it is the
    // one way around the second factor.
    const resetTwoFactor = async (u) => {
        const reason = window.prompt(
            `Remove two-factor for ${u.username}?\n\nDo this only after confirming it is really them (call them back on a number you already have). ` +
            `They will sign in with their password alone and set it up again on the new phone.\n\nReason, for the log:`,
            'lost phone — confirmed by phone call');
        if (!reason) return;
        try { const r = await api.post(`/api/users/${u.id}/reset-2fa`, { reason }); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    const savePolicy = async (policy) => {
        const words = { admins: 'REQUIRED for staff (admin) logins and managing brokers', everyone: 'REQUIRED for every login' };
        if (!window.confirm(`Make two-factor sign-in ${words[policy]}?`
            + '\n\nAnyone covered who has not set it up will be taken to the setup screen at their next sign-in.')) return;
        try { const r = await api.put('/api/users/two-factor-policy', { policy }); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    const revoke = async (u) => {
        if (!window.confirm(`Revoke access for ${u.username}?\n\nThey can no longer sign in. Their employee or agent record, and everything they did, is untouched.`)) return;
        try { const r = await api.post(`/api/users/${u.id}/revoke`, {}); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    // O6: end every session of this login now; the password stays as it is.
    const signOut = async (u) => {
        if (!window.confirm(`Sign ${u.username} out on every device?\n\nTheir password is unchanged; they can sign in again.`)) return;
        try { const r = await api.post(`/api/users/${u.id}/sign-out`, {}); note(r.message); }
        catch (e) { fail(e); }
    };

    // Password-free sign-in (routes/passwordless.js): that login's trusted Tailscale devices
    // and passkeys, each removable at once.
    const openPwFree = async (u) => {
        try { setPwFree({ user: u, ...(await api.get(`/api/auth/passwordless/users/${u.id}`)) }); }
        catch (e) { fail(e); }
    };
    const removePwFree = async (path, what) => {
        if (!window.confirm(`Remove ${what}? It can no longer sign ${pwFree.user.username} in without a password.`)) return;
        try { const r = await api.delete(path); note(r.message); await openPwFree(pwFree.user); }
        catch (e) { fail(e); }
    };

    const restore = async (u) => {
        try { const r = await api.post(`/api/users/${u.id}/restore`, {}); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    const btn = 'text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300';
    const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50';
    const input = 'mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
    const label = 'block text-xs font-medium text-slate-600';

    const needsLogin = [
        ...unlinked.employees.map(e => ({ ...e, kind: 'employee' })),
        ...unlinked.agents.map(a => ({ ...a, kind: 'agent' })),
    ];

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Login Accounts</h1>
                    <p className="text-slate-500 text-sm mt-0.5">
                        Who can sign in to the portal. A login is separate from a person's record — revoking access never removes their history.
                    </p>
                </div>
                <button className={btn} onClick={() => openNew()}>+ New login</button>
            </div>

            {error && <div className="mt-4 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
            {flash && <div className="mt-4 px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-700">{flash}</div>}

            {/* Firm-wide two-factor requirement */}
            {tfPolicy && (
                <div className="mt-5 border border-slate-200 bg-white rounded-lg p-4">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <h2 className="text-sm font-semibold text-slate-800">Two-factor sign-in (authenticator app)</h2>
                            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
                                Who must use a code from a phone app after their password. Staff (admin) logins and managing brokers always must (owner's policy); it can be widened to everyone. Anyone can turn it on for themselves under My Security.
                            </p>
                        </div>
                        <select className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
                            value={tfPolicy.policy} disabled={!tfPolicy.available}
                            onChange={e => savePolicy(e.target.value)}>
                            <option value="admins">Required: staff (admin) logins + managing brokers</option>
                            <option value="everyone">Required: everyone</option>
                        </select>
                    </div>
                    {!tfPolicy.available && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">{tfPolicy.unavailableReason}</p>
                    )}
                </div>
            )}

            {/* Agents: invite to the app (a one-time link where they choose their own password). */}
            <div className="mt-5"><AgentInvitesPanel /></div>

            {/* People onboarded but never given a way in */}
            {!!needsLogin.length && (
                <div className="mt-5 border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <h2 className="text-sm font-semibold text-amber-900">No login yet</h2>
                    <p className="text-xs text-amber-800 mt-0.5 mb-2">
                        These people have a record in the system but cannot sign in.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {needsLogin.map(p => (
                            <button key={`${p.kind}-${p.id}`} onClick={() => openNew(p)}
                                className="text-sm px-3 py-1.5 bg-white border border-amber-300 rounded-md hover:bg-amber-100">
                                {p.name}
                                <span className="text-xs text-amber-700 ml-1.5">
                                    {p.kind === 'agent' ? 'agent' : (p.staff_role || 'employee')}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-5 bg-white border border-slate-200 rounded-lg overflow-x-auto">
                <div className="p-3"><DataList rows={users} rowKey="id" csvName="user-logins" defaultSort={{ key: 'username', dir: 'asc' }} empty="No logins yet."
                    filters={[{ key: 'role', label: 'Role' }, { key: 'status', label: 'Status' }, { key: 'two_factor', label: 'Two-factor' }]}
                    rowClass={(u) => (u.status !== 'active' ? 'opacity-70' : '')}
                    columns={[
                        { key: 'username', label: 'Username', value: (u) => [u.username, u.email].filter(Boolean).join(' '), sortValue: (u) => u.username,
                            render: (u) => <><span className="font-medium">{u.username}</span>
                                {!!u.must_change_password && <div className="text-xs text-amber-700">must change password at next sign-in</div>}
                                {u.email && <div className="text-xs text-slate-400">{u.email}</div>}</> },
                        { key: 'person', label: 'Person', render: (u) => <>
                            {u.person || <span className="text-amber-600 text-xs">not linked to anyone</span>}
                            {u.person && (
                                <div className="text-xs text-slate-400">
                                    {u.person_type}
                                    {u.is_designated_manager || u.is_managing_broker ? ' · managing broker' : ''}
                                    {u.staff_role ? ` · ${u.staff_role}` : ''}
                                </div>
                            )}</> },
                        { key: 'role', label: 'Role', render: (u) => <>{u.role}
                            {u.examiner_org && <div className="text-xs text-slate-400">{u.examiner_org}</div>}
                            {u.stay_signed_in_days && <div className="text-xs text-slate-400">stays signed in {u.stay_signed_in_days} days</div>}
                            {u.access_expires_at && <div className="text-xs text-slate-400">expires {fmtDate(u.access_expires_at)}</div>}</> },
                        // Job access profiles (26 Oct 2026): which areas this login's menu shows.
                        { key: 'job_profile', label: 'Job access', value: (u) => u.job_profile || (u.role === 'examiner' ? 'inspection grants' : 'as role'), render: (u) => (
                            u.role === 'examiner' ? <span className="text-xs text-slate-400">inspection grants</span>
                                : (u.is_designated_manager || u.is_managing_broker) ? <span className="text-xs text-slate-400">everything</span>
                                    : <Link to={`/dashboard/job-access?user=${u.id}`} className="text-blue-600 hover:underline text-sm">
                                        {u.job_profile || 'as role'}</Link>) },
                        { key: 'status', label: 'Status', render: (u) => <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_TONE[u.status]}`}>{u.status}</span> },
                        { key: 'two_factor', label: 'Two-factor', value: (u) => (u.two_factor_enabled ? 'on' : u.two_factor_required ? 'required — not set up' : 'off'), render: (u) => <>
                            {u.two_factor_enabled
                                ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">on</span>
                                : <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.two_factor_required ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>
                                    {u.two_factor_required ? 'required — not set up' : 'off'}</span>}
                            {u.two_factor_locked && <div className="text-xs text-red-600">locked (wrong codes)</div>}</> },
                        { key: 'created_at', label: 'Created', type: 'date', render: (u) => <span className="text-slate-500">{fmtDate(u.created_at)}</span> },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (u) => (
                            <span className="whitespace-nowrap">
                                <button onClick={() => setForm({ ...u })} className="text-blue-600 hover:underline mr-3">Edit</button>
                                <button onClick={() => resetPassword(u)} className="text-blue-600 hover:underline mr-3">Reset password</button>
                                <button onClick={() => signOut(u)} className="text-blue-600 hover:underline mr-3">Sign out this person</button>
                                {u.two_factor_enabled && <button onClick={() => resetTwoFactor(u)} className="text-blue-600 hover:underline mr-3">Reset 2FA</button>}
                                {u.role !== 'examiner' && <button onClick={() => openPwFree(u)} className="text-blue-600 hover:underline mr-3">Password-free</button>}
                                {u.status === 'revoked'
                                    ? <button onClick={() => restore(u)} className="text-green-700 hover:underline">Restore</button>
                                    : <button onClick={() => revoke(u)} className="text-red-600 hover:underline">Revoke</button>}
                            </span>) },
                    ]} /></div>
            </div>

            {/* ---------------- CREATE / EDIT ---------------- */}
            <Modal open={!!form} onClose={() => setForm(null)} size="md"
                title={form?.id ? `Edit login — ${form.username}` : 'Create a login'}
                footer={<>
                    <button className={ghost} onClick={() => setForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy || !form?.username} onClick={save}>
                        {busy ? 'Saving…' : (form?.id ? 'Save' : 'Create login')}
                    </button>
                </>}>
                {form && (
                    <div className="space-y-3">
                        {form._personName && (
                            <p className="text-sm text-slate-600">
                                Creating a login for <strong>{form._personName}</strong>.
                            </p>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={label}>Username</label>
                                <input className={input} value={form.username}
                                    onChange={e => setForm({ ...form, username: e.target.value })} /></div>
                            <div><label className={label}>Email</label>
                                <input className={input} value={form.email || ''}
                                    onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                        </div>

                        <div><label className={label}>Role</label>
                            <select className={input} value={form.role}
                                onChange={e => setForm({ ...form, role: e.target.value })}>
                                <option value="admin">Staff (admin)</option>
                                <option value="licensee">Agent (licensee)</option>
                                <option value="examiner">IDFPR examiner (read-only)</option>
                            </select>
                            <p className="text-xs text-slate-500 mt-1">{ROLE_HELP[form.role]}</p>
                        </div>

                        {form.role === 'examiner' && (
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className={label}>Examiner organisation</label>
                                    <input className={input} value={form.examiner_org || ''}
                                        onChange={e => setForm({ ...form, examiner_org: e.target.value })} /></div>
                                <div><label className={label}>Access expires</label>
                                    <input type="date" className={input}
                                        value={form.access_expires_at ? String(form.access_expires_at).slice(0, 10) : ''}
                                        onChange={e => setForm({ ...form, access_expires_at: e.target.value })} /></div>
                            </div>
                        )}

                        {form.id && form.role !== 'examiner' && (
                            <div>
                                <label className={label}>Stay signed in</label>
                                <select className={input} value={form.stay_signed_in_days || ''}
                                    onChange={e => setForm({ ...form, stay_signed_in_days: e.target.value })}>
                                    <option value="">Normal — sign in again every 24 hours</option>
                                    <option value="30">30 days on each phone or computer</option>
                                    <option value="90">90 days on each phone or computer</option>
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                    After signing in once with the password, this person stays signed in on that device. Takes effect at the next sign-in.
                                    If a phone or computer is lost, use "Sign out this person" to end every session at once.
                                </p>
                            </div>
                        )}

                        {form.role !== 'examiner' && (
                            <div>
                                <label className={label}>Link to a person</label>
                                <select className={input}
                                    value={form.employee_id ? `e${form.employee_id}` : (form.agent_id ? `a${form.agent_id}` : '')}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setForm({
                                            ...form,
                                            employee_id: v.startsWith('e') ? v.slice(1) : '',
                                            agent_id: v.startsWith('a') ? v.slice(1) : '',
                                        });
                                    }}>
                                    <option value="">— not linked —</option>
                                    <optgroup label="Employees without a login">
                                        {unlinked.employees.map(p => <option key={`e${p.id}`} value={`e${p.id}`}>{p.name} — {p.detail || p.staff_role}</option>)}
                                    </optgroup>
                                    <optgroup label="Agents without a login">
                                        {unlinked.agents.map(p => <option key={`a${p.id}`} value={`a${p.id}`}>{p.name}</option>)}
                                    </optgroup>
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                    The link is what gives the login its permissions — an employee's staff role, or an agent's own records.
                                    A login with nobody attached can sign in but will see very little.
                                </p>
                            </div>
                        )}

                        {!form.id && (
                            <p className="text-xs text-slate-500 border-t border-slate-200 pt-3">
                                A temporary password is generated and shown once after you save. They must change it at first sign-in.
                            </p>
                        )}
                    </div>
                )}
            </Modal>

            {/* ---------------- PASSWORD-FREE SIGN-IN OF ONE LOGIN ---------------- */}
            <Modal open={!!pwFree} onClose={() => setPwFree(null)} size="md"
                title={pwFree ? `Password-free sign-in — ${pwFree.user.username}` : ''}
                footer={<button className={ghost} onClick={() => setPwFree(null)}>Close</button>}>
                {pwFree && (
                    <div className="space-y-4 text-sm">
                        <div>
                            <div className="font-semibold text-slate-800 mb-1">Trusted devices on Tailscale</div>
                            {pwFree.devices.length === 0 ? <p className="text-slate-500">None.</p> : (
                                <ul className="space-y-1">{pwFree.devices.map(d => (
                                    <li key={d.device_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-1">
                                        <span>{d.name} <span className="text-xs text-slate-500">({d.tailnet_ip} · {d.status} · last used {d.last_used_at ? fmtDate(d.last_used_at) : 'never'})</span></span>
                                        {(d.status === 'active' || d.status === 'pending') && (
                                            <button className="text-red-600 hover:underline" onClick={() => removePwFree(`/api/auth/tailnet/devices/${d.device_id}`, `the device "${d.name}"`)}>Remove</button>
                                        )}
                                    </li>
                                ))}</ul>
                            )}
                        </div>
                        <div>
                            <div className="font-semibold text-slate-800 mb-1">Passkeys</div>
                            {pwFree.passkeys.length === 0 ? <p className="text-slate-500">None.</p> : (
                                <ul className="space-y-1">{pwFree.passkeys.map(c => (
                                    <li key={c.passkey_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-1">
                                        <span>{c.name || 'Passkey'} <span className="text-xs text-slate-500">(added {fmtDate(c.created_at)} · last used {c.last_used_at ? fmtDate(c.last_used_at) : 'never'})</span></span>
                                        <button className="text-red-600 hover:underline" onClick={() => removePwFree(`/api/auth/passkeys/${c.passkey_id}`, `the passkey "${c.name || 'Passkey'}"`)}>Remove</button>
                                    </li>
                                ))}</ul>
                            )}
                        </div>
                        <p className="text-xs text-slate-500">
                            Approvals, the site switches and every device in the office are on Admin &amp; Settings → Password-free Sign-in.
                            To shut someone out entirely, use Revoke or Sign out this person here as usual.
                        </p>
                    </div>
                )}
            </Modal>

            {/* ---------------- CREDENTIALS, SHOWN ONCE ---------------- */}
            <Modal open={!!credentials} onClose={() => setCredentials(null)} size="sm" title="Temporary password"
                footer={<button className={btn} onClick={() => setCredentials(null)}>Done — I have written it down</button>}>
                {credentials && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-700">{credentials.message}</p>
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
                            <div>
                                <div className="text-xs text-slate-500">Username</div>
                                <div className="font-mono text-sm font-semibold">{credentials.username}</div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Temporary password</div>
                                <div className="font-mono text-base font-semibold select-all">{credentials.temporary_password}</div>
                            </div>
                        </div>
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                            This is shown once and is not stored anywhere. If it is lost, reset the password again rather than guessing.
                        </p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default UserAccountsPage;
